import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/authService';
import { sensorService } from '../services/sensorService';
import { growerService } from '../services/growerService';
import { locationService } from '../services/locationService';
import { userService, DashboardSavedFilter } from '../services/userService';
import Layout from '../components/Layout';
import ReadingsChart from '../components/ReadingsChart';
import ComparisonChart from '../components/ComparisonChart';
import { User, Sensor, SensorReading, Grower, DashboardPreferences, Location } from '../types';
import { getStoredTempUnit, convertTemperature, tempUnitLabel } from '../utils/temperatureUtils';
import { computeFillPct, computeWaterLevel } from '../utils/dds45Utils';

const INITIAL_DISPLAY_COUNT = 5;

/** Check if a reading has SE0X-LB multi-probe data */
function isSE0XReading(reading: SensorReading): boolean {
  return (
    reading.temp_soil1 != null || reading.water_soil1 != null || reading.conduct_soil1 != null ||
    reading.temp_soil2 != null || reading.water_soil2 != null || reading.conduct_soil2 != null ||
    reading.temp_soil3 != null || reading.water_soil3 != null || reading.conduct_soil3 != null ||
    reading.temp_soil4 != null || reading.water_soil4 != null || reading.conduct_soil4 != null ||
    reading.probe_flags != null
  );
}

/** Get active probes from a reading */
function getActiveProbes(reading: SensorReading): number[] {
  const probes: number[] = [];
  if (reading.temp_soil1 != null || reading.water_soil1 != null || reading.conduct_soil1 != null) probes.push(1);
  if (reading.temp_soil2 != null || reading.water_soil2 != null || reading.conduct_soil2 != null) probes.push(2);
  if (reading.temp_soil3 != null || reading.water_soil3 != null || reading.conduct_soil3 != null) probes.push(3);
  if (reading.temp_soil4 != null || reading.water_soil4 != null || reading.conduct_soil4 != null) probes.push(4);
  return probes;
}

/** Get probe data from a reading */
function getProbeData(reading: SensorReading, probeNum: number): { temp: number | null | undefined; moisture: number | null | undefined; ec: number | null | undefined } {
  switch (probeNum) {
    case 1: return { temp: reading.temp_soil1, moisture: reading.water_soil1, ec: reading.conduct_soil1 };
    case 2: return { temp: reading.temp_soil2, moisture: reading.water_soil2, ec: reading.conduct_soil2 };
    case 3: return { temp: reading.temp_soil3, moisture: reading.water_soil3, ec: reading.conduct_soil3 };
    case 4: return { temp: reading.temp_soil4, moisture: reading.water_soil4, ec: reading.conduct_soil4 };
    default: return { temp: null, moisture: null, ec: null };
  }
}

const Dashboard: React.FC = () => {
  const { t } = useTranslation();
  const [user, setUser] = useState<User | null>(null);
  const [sensors, setSensors] = useState<Sensor[]>([]);
  const [growers, setGrowers] = useState<Grower[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [latestReadings, setLatestReadings] = useState<{ [key: number]: SensorReading }>({});
  const [loading, setLoading] = useState(true);
  const [selectedSensorId, setSelectedSensorId] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'single' | 'compare'>('single');
  const [comparedSensorIds, setComparedSensorIds] = useState<number[]>([]);
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);

  // Filter / search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filterApplication, setFilterApplication] = useState('all');
  const [filterGrower, setFilterGrower] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterSensorType, setFilterSensorType] = useState('all');
  const [filterLocation, setFilterLocation] = useState('all');
  const [pinnedSensorIds, setPinnedSensorIds] = useState<number[]>([]);
  const [filterPinnedOnly, setFilterPinnedOnly] = useState(false);
  const [showPinPanel, setShowPinPanel] = useState(false);
  const [showAllSensors, setShowAllSensors] = useState(false);

  // Sidebar open/closed (default open on desktop, closed on mobile via CSS)
  const [sidebarOpen, setSidebarOpen] = useState(true);
  // Filter panel expanded/collapsed – collapsed by default, shows only selected filter + search
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Saved preferences state (multi-filter, named)
  const [savedFilters, setSavedFilters] = useState<DashboardSavedFilter[]>([]);
  const [lastUsedFilterName, setLastUsedFilterName] = useState<string | null>(null);
  const [showSaveFilterModal, setShowSaveFilterModal] = useState(false);
  const [filterNameInput, setFilterNameInput] = useState('');
  const [savingFilter, setSavingFilter] = useState(false);
  // 'update' = overwrite active filter, 'new' = save under a new name
  const [saveFilterMode, setSaveFilterMode] = useState<'update' | 'new'>('update');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      // Fetch user, sensors, and saved filters in parallel
      const [currentUser, sensorsData, filterData] = await Promise.all([
        authService.getCurrentUser(),
        sensorService.getSensors(),
        userService.getDashboardSavedFilters().catch(() => null),
      ]);

      setUser(currentUser);
      setSensors(sensorsData);

      // Load growers for admin and multi-grower users (needed by grower filter dropdown)
      if (currentUser.is_admin || (!currentUser.is_admin && !currentUser.is_admin_grower && (currentUser.grower_ids?.length ?? 0) > 1)) {
        growerService.getGrowers().then(setGrowers).catch(() => {});
      }

      // Load locations for filtering
      locationService.getLocations().then(setLocations).catch(() => {});

      // Apply saved filters – auto-apply last-used filter
      if (filterData) {
        setSavedFilters(filterData.saved_filters);
        setLastUsedFilterName(filterData.last_used_filter);
        if (filterData.last_used_filter) {
          const last = filterData.saved_filters.find(f => f.name === filterData.last_used_filter);
          if (last) {
            applyFilter(last);
          }
        }
      }

      // Bulk-fetch all latest readings in one request
      if (sensorsData.length > 0) {
        const readingsList = await sensorService.getLatestReadingsBulk(
          sensorsData.map(s => s.id)
        );
        const readings: { [key: number]: SensorReading } = {};
        readingsList.forEach(r => { readings[r.sensor_id] = r; });
        setLatestReadings(readings);
      }

      // Auto-select first sensor
      if (sensorsData.length > 0) {
        setSelectedSensorId(sensorsData[0].id);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNewReading = (reading: SensorReading) => {
    // Update latestReadings using the reading's sensor_id
    setLatestReadings(prev => ({
      ...prev,
      [reading.sensor_id]: reading
    }));
  };

  const toggleComparedSensor = (sensorId: number) => {
    setComparedSensorIds(prev =>
      prev.includes(sensorId)
        ? prev.filter(id => id !== sensorId)
        : [...prev, sensorId]
    );
  };

  /** Apply a saved filter's values to state */
  const applyFilter = (filter: DashboardSavedFilter) => {
    setSearchTerm(filter.searchTerm);
    setFilterApplication(filter.filterApplication);
    setFilterGrower(filter.filterGrower);
    setFilterStatus(filter.filterStatus);
    setFilterSensorType(filter.filterSensorType);
    const pinned = filter.pinnedSensorIds || [];
    setPinnedSensorIds(pinned);
    if (pinned.length > 0) setFilterPinnedOnly(true);
    else setFilterPinnedOnly(false);
  };

  const openSaveFilterModal = () => {
    setSaveFilterMode(lastUsedFilterName ? 'update' : 'new');
    setFilterNameInput('');
    setShowSaveFilterModal(true);
  };

  const handleSaveFilter = async () => {
    const name = saveFilterMode === 'update' && lastUsedFilterName
      ? lastUsedFilterName
      : filterNameInput.trim();
    if (!name) return;
    setSavingFilter(true);
    const filter: DashboardSavedFilter = {
      name,
      searchTerm,
      filterApplication,
      filterGrower,
      filterStatus,
      filterSensorType,
      pinnedSensorIds,
    };
    try {
      const result = await userService.upsertDashboardSavedFilter(filter);
      setSavedFilters(result.saved_filters);
      setLastUsedFilterName(result.last_used_filter);
      await loadData();
    } catch (err) {
      console.error('Failed to save filter', err);
    } finally {
      setSavingFilter(false);
    }
    setShowSaveFilterModal(false);
    setFilterNameInput('');
  };


  const handleLoadFilter = async (filter: DashboardSavedFilter) => {
    applyFilter(filter);
    setLastUsedFilterName(filter.name);
    try {
      await userService.setLastUsedDashboardFilter(filter.name);
    } catch (err) {
      console.error('Failed to set last used filter', err);
    }
  };

  const handleDeleteSavedFilter = async (name: string) => {
    try {
      const result = await userService.deleteDashboardSavedFilter(name);
      setSavedFilters(result.saved_filters);
      setLastUsedFilterName(result.last_used_filter);
    } catch (err) {
      console.error('Failed to delete filter', err);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterApplication('all');
    setFilterGrower('all');
    setFilterStatus('all');
    setFilterSensorType('all');
    setPinnedSensorIds([]);
    setFilterPinnedOnly(false);
    setShowAllSensors(false);
  };

  const handleViewModeChange = (mode: 'single' | 'compare') => {
    setViewMode(mode);
    if (mode === 'compare') {
      // Auto-select first application that has multiple sensors
      if (comparableApps.length > 0) {
        const firstApp = comparableApps[0];
        setSelectedAppId(firstApp.appId);
        const appSensors = sensorsByApp[firstApp.appId] || [];
        // Pre-select up to first two sensors from that application
        setComparedSensorIds(appSensors.slice(0, 2).map(s => s.id));
      } else {
        setSelectedAppId(null);
        setComparedSensorIds([]);
      }
    } else {
      setSelectedAppId(null);
    }
  };

  const handleAppChange = (appId: string) => {
    setSelectedAppId(appId);
    const appSensors = sensorsByApp[appId] || [];
    // Clear current selections and pre-select first two sensors from new app
    setComparedSensorIds(appSensors.slice(0, 2).map(s => s.id));
  };

  const selectedSensor = sensors.find(s => s.id === selectedSensorId);
  const isAdmin = user?.is_admin;
  const isAdminGrower = !isAdmin && (user?.is_admin_grower ?? false);
  const hasNoGrowerAccess = !isAdmin && !isAdminGrower && (!user?.grower_ids || user.grower_ids.length === 0);
  const isMultiGrower = !isAdmin && !isAdminGrower && (user?.grower_ids?.length ?? 0) > 1;

  const sensorNames = useMemo(
    () => Object.fromEntries(sensors.map(s => [s.id, s.name])),
    [sensors]
  );

  // Apply search + filter to produce the filtered list
  const filteredSensors = useMemo(() => {
    return sensors.filter(sensor => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        !searchTerm ||
        sensor.name.toLowerCase().includes(searchLower) ||
        sensor.device_id.toLowerCase().includes(searchLower);

      const matchesApplication =
        !isAdmin ||
        filterApplication === 'all' ||
        (filterApplication === 'unassigned' && !sensor.tti_app_id) ||
        sensor.tti_app_id === filterApplication;

      const matchesGrower =
        !(isAdmin || isMultiGrower) ||
        filterGrower === 'all' ||
        (filterGrower === 'unassigned' && !sensor.grower_id) ||
        sensor.grower_id?.toString() === filterGrower;

      const matchesStatus =
        !isAdmin ||
        filterStatus === 'all' ||
        (filterStatus === 'active' && sensor.is_active) ||
        (filterStatus === 'inactive' && !sensor.is_active);

      const matchesSensorType =
        filterSensorType === 'all' ||
        sensor.sensor_type === filterSensorType;

      const matchesLocation =
        filterLocation === 'all' ||
        (filterLocation === 'unassigned' && !sensor.location_id) ||
        sensor.location_id?.toString() === filterLocation;

      const matchesPinned =
        !filterPinnedOnly ||
        pinnedSensorIds.includes(sensor.id);

      return matchesSearch && matchesApplication && matchesGrower && matchesStatus && matchesSensorType && matchesLocation && matchesPinned;
    });
  }, [sensors, searchTerm, filterApplication, filterGrower, filterStatus, filterSensorType, filterLocation, filterPinnedOnly, pinnedSensorIds, isAdmin, isMultiGrower]);

  const hasActiveFilter =
    searchTerm !== '' ||
    (isAdmin && (filterApplication !== 'all' || filterGrower !== 'all' || filterStatus !== 'all')) ||
    (isMultiGrower && filterGrower !== 'all') ||
    filterSensorType !== 'all' ||
    filterLocation !== 'all' ||
    filterPinnedOnly;

  // Show first 5 (most recently added) unless user expanded or has an active filter
  const displayedSensors = useMemo(() => {
    if (showAllSensors || hasActiveFilter) return filteredSensors;
    return filteredSensors.slice(0, INITIAL_DISPLAY_COUNT);
  }, [filteredSensors, showAllSensors, hasActiveFilter]);

  // Unique applications for filter dropdown
  const uniqueApplications = useMemo(
    () =>
      Array.from(new Set(sensors.map(s => s.tti_app_id).filter(Boolean))) as string[],
    [sensors]
  );

  // Unique sensor types for filter dropdown
  const uniqueSensorTypes = useMemo(
    () => Array.from(new Set(sensors.map(s => s.sensor_type).filter(Boolean))) as string[],
    [sensors]
  );

  // Group displayed sensors by TTI application (used for compare mode)
  const sensorsByApp = useMemo(() => {
    const grouped: { [appId: string]: Sensor[] } = {};
    filteredSensors.forEach(sensor => {
      const appId = sensor.tti_app_id || 'unassigned';
      if (!grouped[appId]) grouped[appId] = [];
      grouped[appId].push(sensor);
    });
    return grouped;
  }, [filteredSensors]);

  // Get list of applications that have multiple sensors (can be compared)
  const comparableApps = useMemo(() => {
    return Object.entries(sensorsByApp)
      .filter(([_, appSensors]) => appSensors.length > 1)
      .map(([appId, appSensors]) => ({
        appId,
        sensorCount: appSensors.length,
        displayName: appId === 'unassigned' ? t('dashboard.unassignedSensors') : appId
      }));
  }, [sensorsByApp, t]);

  // Get sensors available for comparison (filtered by selected app)
  const sensorsForComparison = useMemo(() => {
    if (!selectedAppId || viewMode !== 'compare') {
      return filteredSensors;
    }
    return sensorsByApp[selectedAppId] || [];
  }, [selectedAppId, viewMode, filteredSensors, sensorsByApp]);

  if (loading) {
    return (
      <Layout user={user || undefined}>
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600">{t('common.loading')}</div>
        </div>
      </Layout>
    );
  }

  if (hasNoGrowerAccess) {
    return (
      <Layout user={user || undefined}>
        <div className="py-12 flex flex-col items-center text-center">
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-6 py-5 rounded-lg max-w-md">
            <svg className="w-10 h-10 mx-auto mb-3 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 className="text-lg font-semibold mb-2">{t('dashboard.noSensorAccess')}</h3>
            <p className="text-sm">{t('dashboard.noSensorAccessMessage')}</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user || undefined}>
      <div className="py-6">
        {/* Page header */}
        <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
          <div className="flex flex-wrap items-center gap-2">
            {/* Sidebar toggle */}
            <button
              onClick={() => setSidebarOpen(v => !v)}
              className="p-2 rounded-md border border-gray-300 bg-white hover:bg-gray-50 transition-colors"
              title={sidebarOpen ? t('dashboard.filters') : t('dashboard.filters')}
            >
              {sidebarOpen ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
            <h2 className="text-2xl font-semibold text-gray-900">{t('dashboard.title')}</h2>
            {lastUsedFilterName && (
              <span className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-primary-50 text-primary-700 border border-primary-200 rounded-full">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                </svg>
                {t('dashboard.activeFilter')} <strong>{lastUsedFilterName}</strong>
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {isAdmin && (
              <>
                <Link to="/growers" className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                  {t('dashboard.manageGrowers')}
                </Link>
                <Link to="/sensors" className="px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors">
                  {t('dashboard.manageSensors')}
                </Link>
              </>
            )}
            {isAdminGrower && (
              <Link to="/growers" className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                {t('dashboard.manageSubGrowers')}
              </Link>
            )}
            {!isAdmin && !isAdminGrower && (user?.grower_ids?.length ?? 0) > 0 && (
              <Link to="/sensors" className="px-3 py-2 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors">
                {t('dashboard.viewSensors')}
              </Link>
            )}
          </div>
        </div>

        {sensors.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-6 text-center">
            <p className="text-gray-500 mb-4">
              {isAdmin ? t('dashboard.noSensorsAdmin') : t('dashboard.noSensorsUser')}
            </p>
            {isAdmin && (
              <Link to="/sensors" className="inline-block px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700">
                {t('sensors.addSensor')}
              </Link>
            )}
          </div>
        ) : (
          <div className="flex flex-col md:flex-row gap-4 items-start">

            {/* ── LEFT SIDEBAR ─────────────────────────────────────── */}
            {sidebarOpen && (
              <aside className="w-full md:w-72 md:flex-shrink-0 space-y-4">

                {/* Filter panel */}
                <div className="bg-white shadow rounded-lg p-4">
                  {/* Header row */}
                  <div className="flex items-center justify-between mb-3">
                    <button
                      onClick={() => setFiltersExpanded(v => !v)}
                      className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors"
                    >
                      <svg
                        className={`w-3.5 h-3.5 transition-transform duration-200 ${filtersExpanded ? 'rotate-90' : ''}`}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      {t('dashboard.filters')}
                      {hasActiveFilter && !filtersExpanded && (
                        <span className="ml-1 w-2 h-2 rounded-full bg-primary-500 inline-block" title={t('dashboard.activeFilter')} />
                      )}
                    </button>
                    <div className="flex items-center gap-1.5">
                      {/* Save Filter */}
                      <button
                        onClick={openSaveFilterModal}
                        className="px-2 py-1 text-xs bg-primary-600 text-white rounded hover:bg-primary-700 transition-colors flex items-center gap-1"
                        title={t('dashboard.saveFilter')}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                        </svg>
                        {t('dashboard.saveFilter')}
                      </button>
                      {/* Saved Filters dropdown */}
                      {savedFilters.length > 0 && (
                        <div className="relative group">
                          <button className="px-2 py-1 text-xs bg-white border border-gray-300 text-gray-600 rounded hover:bg-gray-50 transition-colors flex items-center gap-1">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                            </svg>
                            {t('dashboard.load')}
                          </button>
                          <div className="absolute left-0 mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-lg z-20 hidden group-hover:block group-focus-within:block">
                            {savedFilters.map(filter => (
                              <div
                                key={filter.name}
                                className={`flex items-center justify-between px-3 py-2 hover:bg-gray-50 ${filter.name === lastUsedFilterName ? 'bg-primary-50' : ''}`}
                              >
                                <button
                                  onClick={() => handleLoadFilter(filter)}
                                  className={`text-xs flex-1 text-left truncate ${filter.name === lastUsedFilterName ? 'text-primary-700 font-medium' : 'text-gray-700'}`}
                                  title={filter.name}
                                >
                                  {filter.name === lastUsedFilterName && <span className="mr-1">✓</span>}
                                  {filter.name}
                                </button>
                                <button
                                  onClick={() => handleDeleteSavedFilter(filter.name)}
                                  className="ml-2 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                                  title={t('common.delete')}
                                >
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Always visible: selected filter label */}
                  {lastUsedFilterName && (
                    <div className="mb-3 flex items-center gap-1.5 px-2.5 py-1.5 bg-primary-50 border border-primary-200 rounded-md text-xs text-primary-700 font-medium">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
                      </svg>
                      <span>{t('dashboard.activeFilter')} <strong>{lastUsedFilterName}</strong></span>
                    </div>
                  )}

                  {/* Always visible: search */}
                  <div className="relative">
                    <input
                      type="text"
                      placeholder={t('dashboard.searchSensors')}
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      className="block w-full border border-gray-300 rounded-md pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                    />
                    <svg className="absolute left-2.5 top-2 h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>

                  {/* Expandable section */}
                  {filtersExpanded && (
                    <div className="mt-3 space-y-3">
                      {/* Sensor type */}
                      <select
                        value={filterSensorType}
                        onChange={e => setFilterSensorType(e.target.value)}
                        className="block w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                      >
                        <option value="all">{t('dashboard.allSensorTypes')}</option>
                        {uniqueSensorTypes.map(t_ => <option key={t_} value={t_}>{t_}</option>)}
                      </select>

                      {/* Location filter */}
                      {locations.length > 0 && (
                        <select
                          value={filterLocation}
                          onChange={e => setFilterLocation(e.target.value)}
                          className="block w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                        >
                          <option value="all">{t('locations.allLocations')}</option>
                          <option value="unassigned">{t('dashboard.noLocation')}</option>
                          {locations.map(loc => <option key={loc.id} value={loc.id.toString()}>{loc.name}</option>)}
                        </select>
                      )}

                      {/* Admin-only filters */}
                      {isAdmin && (
                        <>
                          <select
                            value={filterApplication}
                            onChange={e => setFilterApplication(e.target.value)}
                            className="block w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                          >
                            <option value="all">{t('dashboard.allApplications')}</option>
                            <option value="unassigned">{t('sensors.unassigned')}</option>
                            {uniqueApplications.map(app => <option key={app} value={app}>{app}</option>)}
                          </select>
                          <select
                            value={filterStatus}
                            onChange={e => setFilterStatus(e.target.value)}
                            className="block w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                          >
                            <option value="all">{t('dashboard.allStatuses')}</option>
                            <option value="active">{t('common.active')}</option>
                            <option value="inactive">{t('common.inactive')}</option>
                          </select>
                          <select
                            value={filterGrower}
                            onChange={e => setFilterGrower(e.target.value)}
                            className="block w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                          >
                            <option value="all">{t('dashboard.allGrowers')}</option>
                            <option value="unassigned">{t('sensors.unassigned')}</option>
                            {growers.map(g => <option key={g.id} value={g.id.toString()}>{g.name}</option>)}
                          </select>
                        </>
                      )}

                      {/* Grower filter for multi-grower users */}
                      {isMultiGrower && growers.length > 0 && (
                        <select
                          value={filterGrower}
                          onChange={e => setFilterGrower(e.target.value)}
                          className="block w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-primary-500"
                        >
                          <option value="all">{t('dashboard.allGrowers')}</option>
                          {growers.map(g => <option key={g.id} value={g.id.toString()}>{g.name}</option>)}
                        </select>
                      )}

                      {/* Select specific sensors (pin panel) */}
                      <div className="border-t border-gray-100 pt-2">
                        <button
                          onClick={() => setShowPinPanel(v => !v)}
                          className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-gray-900 transition-colors w-full"
                        >
                          <svg className={`w-3 h-3 transition-transform ${showPinPanel ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                          <span className="font-medium">
                            {t('dashboard.selectSpecificSensors')}
                            {pinnedSensorIds.length > 0 && (
                              <span className="ml-1.5 px-1.5 py-0.5 bg-primary-100 text-primary-700 rounded-full text-xs">{pinnedSensorIds.length}</span>
                            )}
                          </span>
                        </button>
                        {showPinPanel && (
                          <div className="mt-2">
                            <div className="flex justify-end gap-2 mb-1">
                              <button onClick={() => setPinnedSensorIds(sensors.map(s => s.id))} className="text-xs text-primary-600 hover:underline">{t('common.all')}</button>
                              <button onClick={() => { setPinnedSensorIds([]); setFilterPinnedOnly(false); }} className="text-xs text-gray-500 hover:underline">{t('common.clear')}</button>
                            </div>
                            <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md p-1.5 space-y-0.5">
                              {sensors.map(sensor => {
                                const isPinned = pinnedSensorIds.includes(sensor.id);
                                return (
                                  <label key={sensor.id} className="flex items-center gap-2 px-1.5 py-1 rounded hover:bg-gray-50 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={isPinned}
                                      onChange={() => setPinnedSensorIds(prev => isPinned ? prev.filter(id => id !== sensor.id) : [...prev, sensor.id])}
                                      className="h-3.5 w-3.5 text-primary-600 border-gray-300 rounded"
                                    />
                                    <span className="text-xs text-gray-700 truncate" title={sensor.name}>{sensor.name}</span>
                                  </label>
                                );
                              })}
                            </div>
                            {pinnedSensorIds.length > 0 && (
                              <label className="flex items-center gap-2 cursor-pointer mt-2">
                                <input
                                  type="checkbox"
                                  checked={filterPinnedOnly}
                                  onChange={e => setFilterPinnedOnly(e.target.checked)}
                                  className="h-3.5 w-3.5 text-primary-600 border-gray-300 rounded"
                                />
                                <span className="text-xs font-medium text-gray-700">{t('dashboard.showSelectedOnly')}</span>
                              </label>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Results count + clear */}
                      {hasActiveFilter && (
                        <div className="flex items-center justify-between border-t border-gray-100 pt-2">
                          <span className="text-xs text-gray-500">{t('dashboard.sensorsCount', { count: filteredSensors.length, total: sensors.length })}</span>
                          <button onClick={handleClearFilters} className="text-xs text-primary-600 hover:underline">{t('dashboard.clearAll')}</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Sensor selector panel */}
                {displayedSensors.length > 0 && (
                  <div className="bg-white shadow rounded-lg p-4">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-semibold text-gray-700">
                        {viewMode === 'single' ? t('dashboard.selectSensorHeading') : t('dashboard.compareSensors')}
                      </h3>
                      <div className="flex rounded border border-gray-300 overflow-hidden text-xs">
                        <button
                          onClick={() => handleViewModeChange('single')}
                          className={`px-2 py-1 transition-colors ${viewMode === 'single' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                        >
                          {t('dashboard.single')}
                        </button>
                        <button
                          onClick={() => handleViewModeChange('compare')}
                          className={`px-2 py-1 border-l border-gray-300 transition-colors ${viewMode === 'compare' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                        >
                          {t('dashboard.compare')}
                        </button>
                      </div>
                    </div>

                    {viewMode === 'single' ? (
                      <div className="space-y-1 max-h-80 overflow-y-auto">
                        {displayedSensors.map(sensor => (
                          <button
                            key={sensor.id}
                            onClick={() => setSelectedSensorId(sensor.id)}
                            className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                              selectedSensorId === sensor.id
                                ? 'bg-primary-600 text-white'
                                : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            <div className="font-medium truncate">{sensor.name}</div>
                            {latestReadings[sensor.id] && (
                              <div className={`text-xs mt-0.5 ${selectedSensorId === sensor.id ? 'text-primary-200' : 'text-gray-400'}`}>
                                {latestReadings[sensor.id].temperature?.toFixed(1) != null
                                  ? `${(convertTemperature(latestReadings[sensor.id].temperature, getStoredTempUnit()) ?? 0).toFixed(1)}${tempUnitLabel(getStoredTempUnit())}`
                                  : ''}
                              </div>
                            )}
                          </button>
                        ))}
                        {!hasActiveFilter && filteredSensors.length > INITIAL_DISPLAY_COUNT && (
                          <button
                            onClick={() => setShowAllSensors(v => !v)}
                            className="w-full text-xs text-primary-600 hover:underline py-1 text-center"
                          >
                            {showAllSensors ? t('dashboard.showFewer') : t('dashboard.showAllSensors', { count: filteredSensors.length })}
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {isAdmin && comparableApps.length > 0 && (
                          <div className="mb-2">
                            <p className="text-xs text-gray-500 mb-1">{t('dashboard.application')}</p>
                            <div className="space-y-1">
                              {comparableApps.map(app => (
                                <button
                                  key={app.appId}
                                  onClick={() => handleAppChange(app.appId)}
                                  className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${
                                    selectedAppId === app.appId ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                  }`}
                                >
                                  {app.appId === 'unassigned' ? t('dashboard.unassignedSensors') : app.displayName} <span className="opacity-70">({app.sensorCount})</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}
                        <p className="text-xs text-gray-500 mb-1">{t('dashboard.selectSensorsToCompare')}</p>
                        <div className="space-y-1 max-h-64 overflow-y-auto">
                          {sensorsForComparison.map(sensor => {
                            const checked = comparedSensorIds.includes(sensor.id);
                            return (
                              <label key={sensor.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-gray-50 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleComparedSensor(sensor.id)}
                                  className="h-3.5 w-3.5 text-primary-600 border-gray-300 rounded"
                                />
                                <span className="text-xs text-gray-700 truncate">{sensor.name}</span>
                              </label>
                            );
                          })}
                        </div>
                        {comparedSensorIds.length < 2 && (
                          <p className="text-xs text-gray-400">{t('dashboard.selectAtLeast2')}</p>
                        )}
                        {comparableApps.length === 0 && (
                          <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-2">
                            {t('dashboard.needMoreSensors')}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </aside>
            )}

            {/* ── MAIN CONTENT ─────────────────────────────────────── */}
            <div className="flex-1 min-w-0 space-y-6">

              {/* Compare chart */}
              {viewMode === 'compare' && comparedSensorIds.length >= 2 && (
                <div>
                  <div className="bg-white shadow rounded-lg p-6 mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">{t('dashboard.sensorComparison')}</h3>
                    <p className="text-sm text-gray-500">
                      {t('dashboard.comparing')} {comparedSensorIds.map(id => sensorNames[id] ?? `Sensor ${id}`).join(' · ')}
                    </p>
                  </div>
                  <ComparisonChart sensorIds={comparedSensorIds} sensorNames={sensorNames} />
                </div>
              )}

              {/* Single sensor: reading summary + chart */}
              {selectedSensor && viewMode === 'single' && (
                <div>
                  <div className="bg-white shadow rounded-lg p-6 mb-4">
                    <div className="flex flex-wrap justify-between items-start gap-2 mb-4">
                      <div className="min-w-0">
                        <h3 className="text-lg font-semibold text-gray-900 truncate">{selectedSensor.name}</h3>
                        <p className="text-sm text-gray-500 truncate">{selectedSensor.location || t('dashboard.noLocation')}</p>
                      </div>
                      <Link to={`/sensors/${selectedSensor.id}`} className="text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap flex-shrink-0">
                        {t('dashboard.viewDetails')} →
                      </Link>
                    </div>

                    {/* Latest reading */}
                    {latestReadings[selectedSensor.id] && (() => {
                      const reading = latestReadings[selectedSensor.id];
                      const isLeafSensor = reading.leaf_moisture !== null || reading.leaf_temperature !== null;
                      const isSE0X = isSE0XReading(reading);
                      const isDDS45 = reading.distance_mm != null || selectedSensor.sensor_type.includes('DDS45');
                      const isLHT65N = reading.air_temperature != null || reading.air_humidity != null || selectedSensor.sensor_type.includes('LHT65');
                      const isWQSEC = reading.ec_temperature != null || selectedSensor.sensor_type.includes('DR-ECK1.0');
                      const isWQSPH = reading.ph_value != null || reading.ph_temperature != null || selectedSensor.sensor_type.includes('DR-PH01');
                      const isPAR = reading.par != null || selectedSensor.sensor_type.includes('WSC2') || selectedSensor.sensor_type.includes('PAR');

                      if (isPAR) {
                        return (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                            {reading.par != null && (
                              <div className="bg-emerald-50 p-3 rounded-lg text-center">
                                <p className="text-xs text-gray-500">{t('par.par')}</p>
                                <p className="text-xl font-bold text-emerald-700">{reading.par.toFixed(0)} µmol/m²/s</p>
                              </div>
                            )}
                            {reading.battery_voltage != null && (
                              <div className="bg-yellow-50 p-3 rounded-lg text-center">
                                <p className="text-xs text-gray-500">{t('dashboard.battery')}</p>
                                <p className="text-xl font-bold text-yellow-600">{reading.battery_voltage.toFixed(2)}V</p>
                              </div>
                            )}
                          </div>
                        );
                      } else if (isWQSEC) {
                        return (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                            {reading.electrical_conductivity != null && (
                              <div className="bg-cyan-50 p-3 rounded-lg text-center">
                                <p className="text-xs text-gray-500">{t('wqsEc.ec')}</p>
                                <p className="text-xl font-bold text-cyan-700">{reading.electrical_conductivity.toFixed(0)} µS/cm</p>
                              </div>
                            )}
                            {reading.ec_temperature != null && (
                              <div className="bg-red-50 p-3 rounded-lg text-center">
                                <p className="text-xs text-gray-500">{t('wqsEc.probeTemperature')}</p>
                                <p className="text-xl font-bold text-red-600">{(convertTemperature(reading.ec_temperature, getStoredTempUnit()) ?? 0).toFixed(1)}{tempUnitLabel(getStoredTempUnit())}</p>
                              </div>
                            )}
                            {reading.battery_voltage != null && (
                              <div className="bg-yellow-50 p-3 rounded-lg text-center">
                                <p className="text-xs text-gray-500">{t('dashboard.battery')}</p>
                                <p className="text-xl font-bold text-yellow-600">{reading.battery_voltage.toFixed(2)}V</p>
                              </div>
                            )}
                          </div>
                        );
                      } else if (isWQSPH) {
                        return (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                            {reading.ph_value != null && (
                              <div className="bg-cyan-50 p-3 rounded-lg text-center">
                                <p className="text-xs text-gray-500">{t('wqsPh.ph')}</p>
                                <p className="text-xl font-bold text-cyan-700">{reading.ph_value.toFixed(2)}</p>
                              </div>
                            )}
                            {reading.ph_temperature != null && (
                              <div className="bg-red-50 p-3 rounded-lg text-center">
                                <p className="text-xs text-gray-500">{t('wqsPh.probeTemperature')}</p>
                                <p className="text-xl font-bold text-red-600">{(convertTemperature(reading.ph_temperature, getStoredTempUnit()) ?? 0).toFixed(1)}{tempUnitLabel(getStoredTempUnit())}</p>
                              </div>
                            )}
                            {reading.temp_ds18b20 != null && (
                              <div className="bg-red-50 p-3 rounded-lg text-center">
                                <p className="text-xs text-gray-500">{t('wqsPh.waterTemperature')}</p>
                                <p className="text-xl font-bold text-red-600">{(convertTemperature(reading.temp_ds18b20, getStoredTempUnit()) ?? 0).toFixed(1)}{tempUnitLabel(getStoredTempUnit())}</p>
                              </div>
                            )}
                            {reading.battery_voltage != null && (
                              <div className="bg-yellow-50 p-3 rounded-lg text-center">
                                <p className="text-xs text-gray-500">{t('dashboard.battery')}</p>
                                <p className="text-xl font-bold text-yellow-600">{reading.battery_voltage.toFixed(2)}V</p>
                              </div>
                            )}
                          </div>
                        );
                      } else if (isDDS45) {
                        const waterLevelMm = computeWaterLevel(
                          reading.distance_mm,
                          selectedSensor.sensor_height_mm
                        );
                        const fillPct = computeFillPct(
                          reading.distance_mm,
                          selectedSensor.sensor_height_mm,
                          selectedSensor.max_fill_height_mm
                        );
                        return (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                            {user?.is_admin && reading.distance_mm != null && (
                              <div className="bg-blue-50 p-3 rounded-lg text-center">
                                <p className="text-xs text-gray-600">{t('sensorDetail.distance')}</p>
                                <p className="text-xl font-bold text-blue-600">{reading.distance_mm} mm</p>
                              </div>
                            )}
                            {waterLevelMm !== null && (
                              <div className="bg-cyan-50 p-3 rounded-lg text-center">
                                <p className="text-xs text-gray-600">{t('sensorDetail.waterLevel')}</p>
                                <p className="text-xl font-bold text-cyan-600">{(waterLevelMm / 10).toFixed(1)} cm</p>
                              </div>
                            )}
                            {fillPct !== null && (
                              <div className="bg-green-50 p-3 rounded-lg text-center">
                                <p className="text-xs text-gray-600">{t('sensorDetail.fillLevel')}</p>
                                <p className="text-xl font-bold text-green-600">{fillPct.toFixed(1)}%</p>
                              </div>
                            )}
                          </div>
                        );
                      } else if (isLHT65N) {
                        return (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                            {reading.air_temperature != null && (
                              <div className="bg-red-50 p-3 rounded-lg text-center">
                                <p className="text-xs text-gray-600">{t('lht65n.airTemperature')}</p>
                                <p className="text-xl font-bold text-red-600">{(convertTemperature(reading.air_temperature, getStoredTempUnit()) ?? 0).toFixed(1)}{tempUnitLabel(getStoredTempUnit())}</p>
                              </div>
                            )}
                            {reading.air_humidity != null && (
                              <div className="bg-blue-50 p-3 rounded-lg text-center">
                                <p className="text-xs text-gray-600">{t('lht65n.humidity')}</p>
                                <p className="text-xl font-bold text-blue-600">{reading.air_humidity.toFixed(1)}%</p>
                              </div>
                            )}
                            {reading.battery_voltage != null && (
                              <div className="bg-yellow-50 p-3 rounded-lg text-center">
                                <p className="text-xs text-gray-600">{t('dashboard.battery')}</p>
                                <p className="text-xl font-bold text-yellow-600">{reading.battery_voltage.toFixed(2)}V</p>
                              </div>
                            )}
                          </div>
                        );
                      } else if (isSE0X) {
                        const probes = getActiveProbes(reading);
                        return (
                          <div className="mb-4">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs font-medium text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">SE0X-LB</span>
                              <span className="text-xs text-gray-500">{probes.length} {t('sensorDetail.probe')}{probes.length !== 1 ? 's' : ''} active</span>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                              {probes.map(p => {
                                const data = getProbeData(reading, p);
                                return (
                                  <div key={p} className="bg-gray-50 p-3 rounded-lg">
                                    <p className="text-xs font-semibold text-gray-700 mb-2">{t('sensorDetail.probe')} {p}</p>
                                    <div className="grid grid-cols-3 gap-2">
                                      {data.temp != null && <div className="text-center"><p className="text-xs text-gray-500">{t('dashboard.temp')}</p><p className="text-sm font-bold text-blue-600">{(convertTemperature(Number(data.temp), getStoredTempUnit()) ?? 0).toFixed(1)}{tempUnitLabel(getStoredTempUnit())}</p></div>}
                                      {data.moisture != null && <div className="text-center"><p className="text-xs text-gray-500">{t('sensorDetail.moisture')}</p><p className="text-sm font-bold text-green-600">{Number(data.moisture).toFixed(1)}%</p></div>}
                                      {data.ec != null && <div className="text-center"><p className="text-xs text-gray-500">{t('common.ec')}</p><p className="text-sm font-bold text-purple-600">{(Number(data.ec) / 1000).toFixed(2)} mS/cm</p></div>}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                            {reading.battery_voltage != null && <div className="mt-2 text-xs text-gray-500">{t('sensorDetail.batteryValue')} {reading.battery_voltage.toFixed(2)}V</div>}
                          </div>
                        );
                      } else if (isLeafSensor) {
                        return (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                            {reading.leaf_temperature != null && (
                              <div className="bg-blue-50 p-3 rounded-lg text-center">
                                <p className="text-xs text-gray-600">{t('dashboard.leafTemperature')}</p>
                                <p className="text-xl font-bold text-blue-600">{(convertTemperature(reading.leaf_temperature, getStoredTempUnit()) ?? 0).toFixed(1)}{tempUnitLabel(getStoredTempUnit())}</p>
                              </div>
                            )}
                            {reading.leaf_moisture != null && (
                              <div className="bg-green-50 p-3 rounded-lg text-center">
                                <p className="text-xs text-gray-600">{t('dashboard.leafMoisture')}</p>
                                <p className="text-xl font-bold text-green-600">{reading.leaf_moisture.toFixed(1)}%</p>
                              </div>
                            )}
                            {reading.battery_voltage != null && (
                              <div className="bg-yellow-50 p-3 rounded-lg text-center">
                                <p className="text-xs text-gray-600">{t('dashboard.battery')}</p>
                                <p className="text-xl font-bold text-yellow-600">{reading.battery_voltage.toFixed(2)}V</p>
                              </div>
                            )}
                          </div>
                        );
                      } else {
                        return (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-4">
                            <div className="bg-blue-50 p-3 rounded-lg text-center">
                              <p className="text-xs text-gray-600">{t('dashboard.temperature')}</p>
                              <p className="text-xl font-bold text-blue-600">{(convertTemperature(reading.temperature, getStoredTempUnit()) ?? 0).toFixed(1)}{tempUnitLabel(getStoredTempUnit())}</p>
                            </div>
                            <div className="bg-green-50 p-3 rounded-lg text-center">
                              <p className="text-xs text-gray-600">{t('dashboard.moisture')}</p>
                              <p className="text-xl font-bold text-green-600">{reading.soil_moisture?.toFixed(1)}%</p>
                            </div>
                            <div className="bg-purple-50 p-3 rounded-lg text-center">
                              <p className="text-xs text-gray-600">{t('common.ec')}</p>
                              <p className="text-xl font-bold text-purple-600">{reading.electrical_conductivity != null ? (reading.electrical_conductivity / 1000).toFixed(2) : '—'} mS/cm</p>
                            </div>
                          </div>
                        );
                      }
                    })()}
                  </div>

                  <ReadingsChart
                    sensorId={selectedSensor.id}
                    sensorType={selectedSensor.sensor_type}
                    sensor={selectedSensor}
                    latestReading={latestReadings[selectedSensor.id]}
                    onNewReading={handleNewReading}
                  />
                </div>
              )}

              {/* Sensor cards grid (overview) */}
              <div>
                <h3 className="text-base font-semibold text-gray-900 mb-3">{t('dashboard.sensorsOverview')}</h3>

                {filteredSensors.length === 0 ? (
                  <div className="bg-white shadow rounded-lg p-6 text-center">
                    <p className="text-gray-500">{t('dashboard.noSensorsMatchFilter')}</p>
                    <button onClick={handleClearFilters} className="mt-2 text-sm text-primary-600 hover:underline">{t('dashboard.clearFilters')}</button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {displayedSensors.map(sensor => {
                      const reading = latestReadings[sensor.id];
                      const isSelected = sensor.id === selectedSensorId;
                      return (
                        <div
                          key={sensor.id}
                          className={`bg-white overflow-hidden shadow rounded-lg cursor-pointer transition-all ${isSelected ? 'ring-2 ring-primary-500' : 'hover:shadow-md'}`}
                          onClick={() => setSelectedSensorId(sensor.id)}
                        >
                          <div className="px-4 py-4">
                            <div className="flex items-center justify-between mb-3">
                              <Link
                                to={`/sensors/${sensor.id}`}
                                className="text-base font-medium text-primary-600 hover:text-primary-700 truncate"
                                onClick={e => e.stopPropagation()}
                              >
                                {sensor.name}
                              </Link>
                              <span className={`ml-2 flex-shrink-0 px-2 py-0.5 text-xs rounded-full ${sensor.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {sensor.is_active ? t('common.active') : t('common.inactive')}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 mb-3 truncate">{sensor.location || t('dashboard.noLocation')}</p>

                            {reading ? (
                              (() => {
                                const isLeafSensor = reading.leaf_moisture !== null || reading.leaf_temperature !== null;
                                const isSE0X = isSE0XReading(reading);
                                const isDDS45Card = reading.distance_mm != null || sensor.sensor_type.includes('DDS45');
                                const isLHT65NCard = reading.air_temperature != null || reading.air_humidity != null || sensor.sensor_type.includes('LHT65');
                                const isWQSECCard = reading.ec_temperature != null || sensor.sensor_type.includes('DR-ECK1.0');
                                const isWQSPHCard = reading.ph_value != null || reading.ph_temperature != null || sensor.sensor_type.includes('DR-PH01');
                                const isPARCard = reading.par != null || sensor.sensor_type.includes('WSC2') || sensor.sensor_type.includes('PAR');

                                if (isPARCard) {
                                  return (
                                    <div className="space-y-1 text-sm">
                                      {reading.par != null && <div className="flex justify-between"><span className="text-gray-600">{t('par.par')}</span><span className="font-medium text-emerald-700">{reading.par.toFixed(0)} µmol/m²/s</span></div>}
                                      {reading.battery_voltage != null && <div className="flex justify-between"><span className="text-gray-600">{t('dashboard.battery')}</span><span className="font-medium">{reading.battery_voltage.toFixed(2)}V</span></div>}
                                      <p className="text-xs text-gray-400 pt-1">{new Date(reading.timestamp).toLocaleString()}</p>
                                    </div>
                                  );
                                } else if (isWQSECCard) {
                                  return (
                                    <div className="space-y-1 text-sm">
                                      {reading.electrical_conductivity != null && <div className="flex justify-between"><span className="text-gray-600">{t('wqsEc.ec')}</span><span className="font-medium text-cyan-700">{reading.electrical_conductivity.toFixed(0)} µS/cm</span></div>}
                                      {reading.ec_temperature != null && <div className="flex justify-between"><span className="text-gray-600">{t('wqsEc.probeTemperature')}</span><span className="font-medium text-red-600">{(convertTemperature(reading.ec_temperature, getStoredTempUnit()) ?? 0).toFixed(1)}{tempUnitLabel(getStoredTempUnit())}</span></div>}
                                      {reading.battery_voltage != null && <div className="flex justify-between"><span className="text-gray-600">{t('dashboard.battery')}</span><span className="font-medium">{reading.battery_voltage.toFixed(2)}V</span></div>}
                                      <p className="text-xs text-gray-400 pt-1">{new Date(reading.timestamp).toLocaleString()}</p>
                                    </div>
                                  );
                                } else if (isWQSPHCard) {
                                  return (
                                    <div className="space-y-1 text-sm">
                                      {reading.ph_value != null && <div className="flex justify-between"><span className="text-gray-600">{t('wqsPh.ph')}</span><span className="font-medium text-cyan-700">{reading.ph_value.toFixed(2)}</span></div>}
                                      {reading.ph_temperature != null && <div className="flex justify-between"><span className="text-gray-600">{t('wqsPh.probeTemperature')}</span><span className="font-medium text-red-600">{(convertTemperature(reading.ph_temperature, getStoredTempUnit()) ?? 0).toFixed(1)}{tempUnitLabel(getStoredTempUnit())}</span></div>}
                                      {reading.temp_ds18b20 != null && <div className="flex justify-between"><span className="text-gray-600">{t('wqsPh.waterTemperature')}</span><span className="font-medium text-red-600">{(convertTemperature(reading.temp_ds18b20, getStoredTempUnit()) ?? 0).toFixed(1)}{tempUnitLabel(getStoredTempUnit())}</span></div>}
                                      {reading.battery_voltage != null && <div className="flex justify-between"><span className="text-gray-600">{t('dashboard.battery')}</span><span className="font-medium">{reading.battery_voltage.toFixed(2)}V</span></div>}
                                      <p className="text-xs text-gray-400 pt-1">{new Date(reading.timestamp).toLocaleString()}</p>
                                    </div>
                                  );
                                } else if (isDDS45Card) {
                                  const waterLevelMm = computeWaterLevel(
                                    reading.distance_mm,
                                    sensor.sensor_height_mm
                                  );
                                  const fillPct = computeFillPct(
                                    reading.distance_mm,
                                    sensor.sensor_height_mm,
                                    sensor.max_fill_height_mm
                                  );
                                  return (
                                    <div className="space-y-1 text-sm">
                                      {user?.is_admin && reading.distance_mm != null && (
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">{t('sensorDetail.distance')}</span>
                                          <span className="font-medium text-blue-600">{reading.distance_mm} mm</span>
                                        </div>
                                      )}
                                      {waterLevelMm !== null && (
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">{t('sensorDetail.waterLevel')}</span>
                                          <span className="font-medium text-cyan-600">{(waterLevelMm / 10).toFixed(1)} cm</span>
                                        </div>
                                      )}
                                      {fillPct !== null && (
                                        <div className="flex justify-between">
                                          <span className="text-gray-600">{t('sensorDetail.fillLevel')}</span>
                                          <span className="font-medium text-green-600">{fillPct.toFixed(1)}%</span>
                                        </div>
                                      )}
                                      <p className="text-xs text-gray-400 pt-1">{new Date(reading.timestamp).toLocaleString()}</p>
                                    </div>
                                  );
                                } else if (isLHT65NCard) {
                                  return (
                                    <div className="space-y-1 text-sm">
                                      {reading.air_temperature != null && <div className="flex justify-between"><span className="text-gray-600">{t('lht65n.airTemperature')}</span><span className="font-medium text-red-600">{(convertTemperature(reading.air_temperature, getStoredTempUnit()) ?? 0).toFixed(1)}{tempUnitLabel(getStoredTempUnit())}</span></div>}
                                      {reading.air_humidity != null && <div className="flex justify-between"><span className="text-gray-600">{t('lht65n.humidity')}</span><span className="font-medium text-blue-600">{reading.air_humidity.toFixed(1)}%</span></div>}
                                      {reading.battery_voltage != null && <div className="flex justify-between"><span className="text-gray-600">{t('dashboard.battery')}</span><span className="font-medium">{reading.battery_voltage.toFixed(2)}V</span></div>}
                                      <p className="text-xs text-gray-400 pt-1">{new Date(reading.timestamp).toLocaleString()}</p>
                                    </div>
                                  );
                                } else if (isSE0X) {
                                  const probes = getActiveProbes(reading);
                                  return (
                                    <div className="space-y-1">
                                      <div className="flex items-center gap-1 mb-1">
                                        <span className="text-xs text-indigo-700 bg-indigo-50 px-1.5 py-0.5 rounded">SE0X-LB</span>
                                        <span className="text-xs text-gray-400">{probes.length} {t('sensorDetail.probe')}{probes.length !== 1 ? 's' : ''}</span>
                                      </div>
                                      {probes.map(p => {
                                        const data = getProbeData(reading, p);
                                        return (
                                          <div key={p} className="flex justify-between text-xs">
                                            <span className="text-gray-500 font-medium">P{p}</span>
                                            <span>
                                              {data.temp != null && <span className="text-blue-600">{(convertTemperature(Number(data.temp), getStoredTempUnit()) ?? 0).toFixed(1)}{tempUnitLabel(getStoredTempUnit())}</span>}
                                              {data.temp != null && data.moisture != null && <span className="text-gray-300 mx-1">|</span>}
                                              {data.moisture != null && <span className="text-green-600">{Number(data.moisture).toFixed(1)}%</span>}
                                              {(data.temp != null || data.moisture != null) && data.ec != null && <span className="text-gray-300 mx-1">|</span>}
                                              {data.ec != null && <span className="text-purple-600">{Number(data.ec).toFixed(0)}</span>}
                                            </span>
                                          </div>
                                        );
                                      })}
                                      <p className="text-xs text-gray-400 pt-1">{new Date(reading.timestamp).toLocaleString()}</p>
                                    </div>
                                  );
                                } else if (isLeafSensor) {
                                  return (
                                    <div className="space-y-1 text-sm">
                                      {reading.leaf_temperature != null && <div className="flex justify-between"><span className="text-gray-600">{t('dashboard.leafTemp')}</span><span className="font-medium">{(convertTemperature(reading.leaf_temperature, getStoredTempUnit()) ?? 0).toFixed(1)}{tempUnitLabel(getStoredTempUnit())}</span></div>}
                                      {reading.leaf_moisture != null && <div className="flex justify-between"><span className="text-gray-600">{t('dashboard.leafMoisture')}</span><span className="font-medium">{reading.leaf_moisture.toFixed(1)}%</span></div>}
                                      {reading.battery_voltage != null && <div className="flex justify-between"><span className="text-gray-600">{t('dashboard.battery')}</span><span className="font-medium">{reading.battery_voltage.toFixed(2)}V</span></div>}
                                      <p className="text-xs text-gray-400 pt-1">{new Date(reading.timestamp).toLocaleString()}</p>
                                    </div>
                                  );
                                } else {
                                  return (
                                    <div className="space-y-1 text-sm">
                                      <div className="flex justify-between"><span className="text-gray-600">{t('dashboard.temperature')}</span><span className="font-medium">{(convertTemperature(reading.temperature, getStoredTempUnit()) ?? 0).toFixed(1)}{tempUnitLabel(getStoredTempUnit())}</span></div>
                                      <div className="flex justify-between"><span className="text-gray-600">{t('dashboard.moisture')}</span><span className="font-medium">{reading.soil_moisture?.toFixed(1)}%</span></div>
                                      <div className="flex justify-between"><span className="text-gray-600">{t('common.ec')}</span><span className="font-medium">{reading.electrical_conductivity != null ? (reading.electrical_conductivity / 1000).toFixed(2) : '—'} mS/cm</span></div>
                                      <p className="text-xs text-gray-400 pt-1">{new Date(reading.timestamp).toLocaleString()}</p>
                                    </div>
                                  );
                                }
                              })()
                            ) : (
                              <p className="text-sm text-gray-400">{t('dashboard.noReadingsAvailable')}</p>
                            )}

                            {isSelected && (
                              <div className="mt-2 pt-2 border-t border-gray-100">
                                <span className="text-xs text-primary-600 font-medium">{t('dashboard.selectedLabel')}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Save Filter Modal */}
      {showSaveFilterModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-medium text-gray-900 mb-4">{t('dashboard.saveFilterTitle')}</h3>

            {lastUsedFilterName && (
              <div className="flex rounded-lg border border-gray-200 overflow-hidden mb-4">
                <button
                  type="button"
                  onClick={() => setSaveFilterMode('update')}
                  className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${saveFilterMode === 'update' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  {t('dashboard.updateCurrent')}
                </button>
                <button
                  type="button"
                  onClick={() => setSaveFilterMode('new')}
                  className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${saveFilterMode === 'new' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  {t('dashboard.saveAsNew')}
                </button>
              </div>
            )}

            {saveFilterMode === 'update' && lastUsedFilterName ? (
              <p className="text-sm text-gray-600 mb-4">
                {t('dashboard.filterOverwrite', { name: lastUsedFilterName })}
              </p>
            ) : (
              <input
                type="text"
                autoFocus
                placeholder={t('dashboard.enterFilterName')}
                value={filterNameInput}
                onChange={e => setFilterNameInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSaveFilter(); if (e.key === 'Escape') setShowSaveFilterModal(false); }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 mb-4"
              />
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowSaveFilterModal(false)}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSaveFilter}
                disabled={(saveFilterMode === 'new' && !filterNameInput.trim()) || savingFilter}
                className="px-4 py-2 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {savingFilter ? t('dashboard.saving') : saveFilterMode === 'update' && lastUsedFilterName ? t('dashboard.update') : t('common.save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Dashboard;
